import { NgTemplateOutlet } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { SiaSqlEditorComponent } from '@sia/code-editor';
import {
  SiaCheckBoxComponent,
  SiaNumberboxComponent,
  SiaSelectBoxComponent,
  SiaTextareaBoxComponent,
  SiaTextBoxComponent,
} from '@sia/inputs';
import { notify, SiaAccordionComponent, SiaTranslatePipe } from '@sia/shared';

@Component({
  selector: 'sia-workflow-action-configuration',
  imports: [
    SiaTextareaBoxComponent,
    SiaSelectBoxComponent,
    SiaTextBoxComponent,
    SiaNumberboxComponent,
    SiaTranslatePipe,
    MatIcon,
    MatDialogActions,
    MatDialogContent,
    SiaTranslatePipe,
    MatDialogClose,
    MatDialogTitle,
    MatButton,
    SiaAccordionComponent,
    NgTemplateOutlet,
    SiaCheckBoxComponent,
    SiaSqlEditorComponent,
  ],
  templateUrl: './workflow-action-configuration.component.html',
  styleUrl: './workflow-action-configuration.component.scss',
})
export class WorkflowActionConfigurationComponent {
  #dialogData = inject(MAT_DIALOG_DATA);
  #dialogRef = inject(MatDialogRef);

  selectedAction: any = this.#dialogData.selectedAction;
  variables = this.#dialogData.variables;
  roles: any[] = [
    { id: 'system', description: 'system' },
    { id: 'user', description: 'user' },
    { id: 'assistant', description: 'assistant' },
  ];
  sqlResultTypes: any[] = [
    { id: 'single', description: 'Risultato singolo' },
    { id: 'multiple', description: 'Risultato multiplo' },
  ];
  classificationsTypes: any[] = [
    { id: 'single', description: 'Singola categoria' },
    { id: 'multiple', description: 'Multiple categorie' },
  ];
  chromaDBOperation: any[] = [
    { id: 'GET', description: 'Ottieni documenti' },
    { id: 'CREATE', description: 'Crea collezione' },
    { id: 'DELETE', description: 'Elimina documenti' },
    { id: 'INSERT', description: 'Inserire documenti' },
  ];

  outputChromaDBTypes: { id: string; description: string }[] = [
    { id: 'Document', description: 'Documenti' },
    { id: 'Metadata', description: 'Metadati' },
    { id: 'Id', description: 'Id' },
  ];

  fileManagerOperation: any[] = [
    { id: 'Move', description: 'Muovi file/cartella' },
    { id: 'Rename', description: 'Rinomina file/cartella' },
    { id: 'Add', description: 'Aggiungi file/cartella' },
    { id: 'Delete', description: 'Elimina file/cartella' },
    { id: 'Preview', description: 'Genera anteprima file' },
  ];

  validateRequiredField(): boolean | undefined {
    switch (this.selectedAction?.type) {
      case 'SQL':
        return this.selectedAction.query ? true : false;
      case 'Chunking':
        return this.selectedAction.propertyFilePath &&
          this.selectedAction.propertyFileContent
          ? true
          : false;
      case 'Classification':
        return this.selectedAction.classificationType &&
          this.selectedAction.propertyTextToCategorize
          ? true
          : false;
      case 'FileManager':
        return this.selectedAction.operation ? true : false;
      case 'Trigger':
        return this.selectedAction.workflowId ? true : false;
      case 'Chat':
        return this.selectedAction.model &&
          this.selectedAction.prompts &&
          this.selectedAction.prompts.length > 0
          ? true
          : false;
      default:
        return false;
    }
  }
  save() {
    this.removeEmptyFields();
    if (!this.validateRequiredField()) {
      notify('Completare i campi richiesti prima di salvare', 3000, 'warning');
      return;
    }
    this.#dialogRef.close(this.selectedAction);
  }

  removeEmptyFields() {
    if (this.selectedAction.parameters) {
      this.selectedAction.parameters = this.selectedAction.parameters.filter(
        (p: any) => p.name || p.value,
      );
    }
    if (this.selectedAction.additionalMetadatas) {
      this.selectedAction.additionalMetadatas =
        this.selectedAction.additionalMetadatas?.filter(
          (m: any) => m.name || m.value,
        );
    }
    if (this.selectedAction.fieldsToExtract) {
      this.selectedAction.fieldsToExtract =
        this.selectedAction.fieldsToExtract.filter(
          (f: any) => f.id || f.whatToExtract || f.extractionType,
        );
    }
    if (this.selectedAction.prompts) {
      this.selectedAction.prompts = this.selectedAction.prompts.filter(
        (p: any) => p.role || p.content,
      );
    }
  }

  addParameters() {
    if (!this.selectedAction.parameters) {
      this.selectedAction.parameters = [];
    }
    this.selectedAction.parameters?.push({});
  }

  removeParameters(parameter: any) {
    this.selectedAction.parameters = this.selectedAction.parameters?.filter(
      (p: any) => p !== parameter,
    );
  }

  addMetadata() {
    if (!this.selectedAction.additionalMetadatas) {
      this.selectedAction.additionalMetadatas = [];
    }
    this.selectedAction.additionalMetadatas?.push({});
  }

  removeMetadata(parameter: any) {
    this.selectedAction.additionalMetadatas =
      this.selectedAction.additionalMetadatas?.filter(
        (p: any) => p !== parameter,
      );
  }

  addFiledToExtract() {
    if (!this.selectedAction.fieldsToExtract) {
      this.selectedAction.fieldsToExtract = [];
    }
    this.selectedAction.fieldsToExtract?.push({});
  }

  removeFiledToExtract(fieldToExtract: any) {
    this.selectedAction.fieldsToExtract =
      this.selectedAction.fieldsToExtract?.filter(
        (f: any) => f !== fieldToExtract,
      );
  }

  addPrompt() {
    if (!this.selectedAction.prompts) {
      this.selectedAction.prompts = [];
    }
    this.selectedAction.prompts?.push({});
  }

  removePrompt(prompt: any) {
    this.selectedAction.prompts = this.selectedAction.prompts?.filter(
      (f: any) => f !== prompt,
    );
  }

  clearChunkProperty(action: any) {
    if (action.staticValue) action.propertyChunkingStrategy = '';
    else action.chunkingStrategy = '';
  }
}
